import { Alert, Input, Modal, message, Select, Spin, Tag } from 'antd';
import {
  Coins,
  Copy,
  Edit3,
  Plus,
  RefreshCcw,
  Save,
  Trash2,
} from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { ADMIN_PERMISSIONS } from '@/auth/permissions';
import { AppConfigPublishDialog } from '@/components/common/AppConfigPublishDialog';
import { AppConfigVersionPanel } from '@/components/common/AppConfigVersionPanel';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { authStore, llmPricingStore } from '@/stores';
import type { PricingRule } from '@/types';
import { PricingRuleFormDialog } from './components/PricingRuleFormDialog';
import { PricingVersionsDisplay } from './components/PricingVersionsDisplay';

const USD_TO_CNY_RATE = 6.8;

function displayPrice(value: number | undefined) {
  if (value == null) {
    return <span className="text-muted-foreground">未配置</span>;
  }
  return (
    <div>
      <div>${value}/M</div>
      <div className="text-xs text-muted-foreground">
        ≈ ¥{(value * USD_TO_CNY_RATE).toFixed(2)}/M
      </div>
    </div>
  );
}

export const LLMPricingPage = observer(() => {
  const canRead = authStore.can(ADMIN_PERMISSIONS.pricingRead);
  const canWrite = authStore.can(ADMIN_PERMISSIONS.pricingWrite);
  const deploymentPending = llmPricingStore.deploymentPending;

  useEffect(() => {
    if (canRead) {
      void llmPricingStore.loadCatalog();
    }
  }, [canRead]);
  useEffect(() => {
    if (!deploymentPending) return;
    let stopped = false;
    let timer = 0;
    const poll = async () => {
      try {
        await llmPricingStore.loadCatalog();
      } finally {
        if (!stopped && llmPricingStore.deploymentPending) {
          timer = window.setTimeout(() => void poll(), 3000);
        }
      }
    };
    timer = window.setTimeout(() => void poll(), 3000);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [deploymentPending]);
  const [editingRule, setEditingRule] = useState<PricingRule | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [allowIDEdit, setAllowIDEdit] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  const columns = useMemo<Column<PricingRule>[]>(
    () => [
      {
        id: 'id',
        header: '规则ID',
        accessor: (r) => <span className="font-mono text-xs">{r.id}</span>,
      },
      {
        id: 'enabled',
        header: '状态',
        accessor: (r) =>
          r.enabled === false ? <Tag>停用</Tag> : <Tag color="green">启用</Tag>,
      },
      {
        id: 'provider',
        header: 'Provider',
        accessor: (r) => r.provider || '-',
      },
      {
        id: 'route_id',
        header: 'Route ID',
        accessor: (r) => (
          <span className="font-mono text-xs">{r.route_id || '-'}</span>
        ),
      },
      {
        id: 'provider_path',
        header: '路径',
        accessor: (r) => r.provider_path || '-',
      },
      {
        id: 'public_model',
        header: '公共模型',
        accessor: (r) => r.public_model || '-',
      },
      {
        id: 'upstream_model',
        header: '上游模型',
        accessor: (r) => (
          <span className="font-mono text-xs">{r.upstream_model || '-'}</span>
        ),
      },
      {
        id: 'dynamic_pricing',
        header: '动态定价',
        accessor: (r) => (
          <PricingVersionsDisplay
            versions={r.pricing_versions}
            basePrices={r}
          />
        ),
      },
      {
        id: 'input',
        header: '基础输入价',
        accessor: (r) => displayPrice(r.input_usd_per_million_tokens),
      },
      {
        id: 'output',
        header: '基础输出价',
        accessor: (r) => displayPrice(r.output_usd_per_million_tokens),
      },
      {
        id: 'price_priority',
        header: '价格匹配优先级',
        accessor: (r) => r.priority || '-',
      },
      {
        id: 'multiplier',
        header: '倍率',
        accessor: (r) => {
          const m = r.price_multiplier;
          if (m == null || m === 1) return <Tag>1.0</Tag>;
          return <Tag color={m > 1 ? 'orange' : 'green'}>{m}</Tag>;
        },
      },
      {
        id: 'free',
        header: '免费',
        accessor: (r) => (r.free ? <Badge>免费</Badge> : '-'),
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (r) =>
          canWrite ? (
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditingRule(r);
                  setAllowIDEdit(false);
                  setDialogOpen(true);
                }}
              >
                <Edit3 className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditingRule({ ...r, id: `${r.id}-copy` });
                  setAllowIDEdit(true);
                  setDialogOpen(true);
                }}
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  Modal.confirm({
                    title: `删除价格规则 ${r.id}？`,
                    content: '删除只会修改当前草稿，发布前仍会执行联合校验。',
                    okButtonProps: { danger: true },
                    onOk: () => llmPricingStore.deleteRule(r.id),
                  })
                }
              >
                <Trash2 className="h-3.5 w-3.5 text-red-600" />
              </Button>
            </div>
          ) : null,
      },
    ],
    [canWrite],
  );

  const preparePublish = async () => {
    if (!llmPricingStore.catalog) return;
    if (await llmPricingStore.validateCatalog(llmPricingStore.catalog)) {
      setPublishOpen(true);
    }
  };

  const handlePublish = async () => {
    if (!llmPricingStore.catalog) return;
    await llmPricingStore.publishCatalog(llmPricingStore.catalog);
    if (llmPricingStore.publishResult) {
      setPublishOpen(false);
      message.success('价格表已发布');
    }
  };

  if (!canRead) {
    return (
      <div className="p-6">
        <Alert
          type="warning"
          message="当前账号没有查看 LLM 价格表的权限"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="flex items-center gap-2 text-lg font-semibold text-foreground">
            <Coins className="h-5 w-5" /> LLM 价格表
          </h1>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              disabled={llmPricingStore.loading}
              onClick={() => void llmPricingStore.loadCatalog()}
            >
              <RefreshCcw className="mr-2 h-4 w-4" /> 刷新
            </Button>
            {canWrite ? (
              <Button
                variant="outline"
                onClick={() => {
                  setEditingRule({
                    id: '',
                    enabled: true,
                    price_multiplier: 1,
                  });
                  setAllowIDEdit(true);
                  setDialogOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> 新增规则
              </Button>
            ) : null}
            {canWrite ? (
              <Button
                disabled={
                  llmPricingStore.publishing ||
                  deploymentPending ||
                  llmPricingStore.baseVersion <= 0
                }
                onClick={() => void preparePublish()}
              >
                <Save className="mr-2 h-4 w-4" /> 校验并发布
              </Button>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Input.Search
            placeholder="搜索 规则ID/模型/路由"
            allowClear
            style={{ width: 260 }}
            onChange={(e) => llmPricingStore.setKeyword(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Provider"
            style={{ width: 180 }}
            value={llmPricingStore.providerFilter || undefined}
            onChange={(v) => llmPricingStore.setProviderFilter(v || '')}
            options={llmPricingStore.distinctProviders.map((p) => ({
              label: p,
              value: p,
            }))}
          />
          <span className="text-xs text-muted-foreground">
            人民币按 1 USD = {USD_TO_CNY_RATE} CNY 估算
          </span>
        </div>

        {llmPricingStore.error ? (
          <Alert type="error" message={llmPricingStore.error} showIcon />
        ) : null}
        {llmPricingStore.warning ? (
          <Alert type="warning" message={llmPricingStore.warning} showIcon />
        ) : null}

        <Spin spinning={llmPricingStore.loading}>
          <DataTable
            columns={columns}
            data={llmPricingStore.filteredRules}
            loading={llmPricingStore.loading}
            rowKey="id"
            emptyMessage="暂无价格规则"
          />
        </Spin>

        {llmPricingStore.catalog ? (
          <div className="text-xs text-muted-foreground">
            版本: {llmPricingStore.catalog.version} · 规则数:{' '}
            {llmPricingStore.catalog.rules.length}
          </div>
        ) : null}
        <AppConfigVersionPanel
          currentVersion={llmPricingStore.currentVersion}
          hostedVersions={llmPricingStore.recentHostedVersions}
          deployments={llmPricingStore.recentDeployments}
          canRollback={canWrite && !deploymentPending}
          rollingBack={llmPricingStore.publishing}
          onRollback={(version) => llmPricingStore.rollbackCatalog(version)}
        />

        <PricingRuleFormDialog
          open={dialogOpen}
          rule={editingRule}
          allowIDEdit={allowIDEdit}
          onClose={() => setDialogOpen(false)}
          onSave={(updated) => {
            if (updated.id) {
              const exists = llmPricingStore.catalog?.rules.some(
                (rule) => rule.id === updated.id,
              );
              if (allowIDEdit && exists) {
                message.error(`规则 ID ${updated.id} 已存在`);
                return;
              }
              if (allowIDEdit) {
                llmPricingStore.addRule(updated);
              } else {
                llmPricingStore.updateRule(updated.id, updated);
              }
            }
            setDialogOpen(false);
          }}
        />
        <AppConfigPublishDialog
          open={publishOpen}
          title="发布 LLM 价格表"
          before={llmPricingStore.originalCatalog}
          after={llmPricingStore.catalog}
          validation={llmPricingStore.validation}
          publishing={llmPricingStore.publishing}
          deploymentPending={deploymentPending}
          onCancel={() => setPublishOpen(false)}
          onConfirm={() => void handlePublish()}
        />
      </div>
    </div>
  );
});
